import React, { useEffect, useState } from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Section } from '../components/Section';
import { UseCaseAccordion } from '../components/UseCaseAccordion';
import { LimeBandCTA } from '../components/LimeBandCTA';
export function Principles() {
  const [rotation, setRotation] = useState(0);
  useEffect(() => {
    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      const rotationDegrees = scrollPosition * 0.2;
      setRotation(rotationDegrees);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  const accordionItems = [{
    title: 'Clarifying purpose',
    content: 'Helping people understand the why, so they can move with clarity and conviction.'
  }, {
    title: 'Building habits',
    content: 'Creating repeatable behaviors that build culture, not just compliance.'
  }, {
    title: 'Empowering leaders',
    content: 'Enabling leaders to model change in the everyday moments that matter most.'
  }, {
    title: 'Measuring progress',
    content: "Surfacing what's working, what's wobbling, and where to focus next."
  }];
  return <div className="flex flex-col min-h-screen w-full bg-white" data-id="element-449">
      <Navbar data-id="element-450" />
      <main className="flex-grow" data-id="element-451">
        {/* Hero Section */}
        <Section className="pt-32 md:pt-48" data-id="element-452">
          <div className="flex flex-col md:flex-row md:gap-16 lg:gap-32" data-id="element-453">
            <div className="mb-8 md:mb-0 md:w-1/2" data-id="element-454">
              <h1 className="font-poppins font-normal text-3xl md:text-5xl text-black leading-tight mb-6" data-id="element-455">
                Plans don't transform business. People do.
              </h1>
              <div className="space-y-4" data-id="element-456">
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-457">
                  Transformation looks great in PowerPoint. Right up until the
                  writeup becomes reality.
                </p>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-458">
                  That's when things stall: teams get overwhelmed, leaders lose
                  momentum, and agility turns into a checklist. Despite all the
                  planning, investment, and intent, real change often breaks
                  down at the point of behavior.
                </p>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-459">
                  Because transformation isn't a series of org charts, process
                  updates, or new team names. It's a shift in how people think,
                  lead, and work. And that doesn't happen through top-down
                  directives. It happens when people understand the mission,
                  believe in the purpose, and feel equipped to do things
                  differently.
                </p>
                <p className="font-inter text-base leading-relaxed text-black" data-id="element-460">
                  We help you move beyond surface-level change by building the
                  structures, habits, and behaviors that actually stick. So your
                  people aren't just part of the transformation. They power it.
                </p>
              </div>
            </div>
            <div className="md:w-1/2 overflow-hidden rounded-lg aspect-[4/3]" data-id="element-461">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/6tCzjAzuXLviX9vWgTkKyg/Principles_Image_1.jpg" alt="Macro succulent with geometric green pattern" className="w-full h-full object-cover" data-id="element-462" />
            </div>
          </div>
        </Section>
        {/* POV Section */}
        <Section data-id="element-463">
          <div className="max-w-4xl mx-auto text-center" data-id="element-464">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-465">
              Our take.
            </h2>
            <div className="space-y-4" data-id="element-466">
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-467">
                Transformation takes more than a plan. It requires more than a
                new org structure, a fresh operating model, or a carefully
                crafted strategy deck. It needs momentum.
              </p>
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-468">
                When momentum builds. When behaviors shift. When the purpose
                behind the plan actually lands. That's where we focus.
              </p>
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-469">
                We work on the human side of change, where transformation moves
                from theory to traction. Where teams build new habits. Where
                leaders show up differently. Where change stops being something
                to survive and starts being an opportunity to thrive.
              </p>
              <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-470">
                Because lasting transformation doesn't roll out from the top
                down. It moves from the inside out.
              </p>
            </div>
          </div>
        </Section>
        {/* Full Width Image */}
        <Section className="py-8 md:py-12" data-id="element-471">
          <div className="max-w-[170px] mx-auto" data-id="element-472">
            <div className="w-full aspect-square overflow-hidden rounded-lg" data-id="element-473">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/8JnCnwFgpu5hq3nksuUQDp/Home_Image_2.png" alt="Abstract geometric pattern" className="w-full h-full object-cover" style={{
              transform: `rotate(${rotation}deg)`,
              transition: 'transform 0.1s linear'
            }} data-id="element-474" />
            </div>
          </div>
        </Section>
        {/* Solution Section with Accordion */}
        <Section bgColor="lightgray" data-id="element-475">
          <div className="flex flex-col md:flex-row md:gap-16 lg:gap-32" data-id="element-476">
            <div className="mb-8 md:mb-0 md:w-1/2" data-id="element-477">
              <h2 className="font-poppins font-normal text-2xl md:text-3xl text-black mb-4" data-id="element-478">
                Momentum, not models.
              </h2>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-479">
                We don't install frameworks. We help you design experiences that
                shift behavior, build alignment, and accelerate momentum,
                zeroing in on the areas that shape how people show up, adapt,
                and move forward. Together.
              </p>
            </div>
            <div className="md:w-1/2" data-id="element-480">
              <UseCaseAccordion items={accordionItems} data-id="element-481" />
            </div>
          </div>
        </Section>
        {/* Offering Section */}
        <Section data-id="element-482">
          <div className="max-w-4xl mx-auto text-center mb-8 md:mb-12" data-id="element-483">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-484">
              How we help.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-485">
              We don't believe in one-size-fits-all. Some teams need leadership
              support. Others need better teaming strategies, stronger
              measurement, or a roadmap they can actually use. We meet you where
              you are and plug in where we can make the biggest impact.
            </p>
          </div>
          {/* Offering Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 md:gap-8" data-id="element-486">
            {/* Row 1: Image 3 | Team Design */}
            <div className="overflow-hidden rounded-lg aspect-[4/3]" data-id="element-487">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/tiLRtEyMVE8xiRwVcdTXb3/Principles_Image_3.jpg" alt="Cactus macro pattern with geometric green texture" className="w-full h-full object-cover" data-id="element-488" />
            </div>
            <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col justify-center" data-id="element-489">
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-490">
                Team Design & Activation
              </h3>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-491">
                Setting teams up to move with clarity, confidence, and purpose.
              </p>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-492">
                We help you structure internal networks, launch new teams, and
                create the conditions for shared ownership and real progress.
              </p>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-493">
                Services: Network Design, Teaming Strategies, Agile Kickoffs,
                and Roadmapping.
              </p>
            </div>
            {/* Row 2: Image 4 | Leadership */}
            <div className="overflow-hidden rounded-lg aspect-[4/3]" data-id="element-494">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dEpkurhi6bV5wNsHGgwEpV/Principles_Image_4.jpg" alt="Silhouetted figure in dramatic lighting with teal accent" className="w-full h-full object-cover" data-id="element-495" />
            </div>
            <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col justify-center" data-id="element-496">
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-497">
                Leadership & Culture
              </h3>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-498">
                Helping leaders show up differently—and bring others with them.
              </p>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-499">
                We design experiences that build the skills, mindset, and
                momentum required to lead change from the inside out.
              </p>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-500">
                Services: Leadership Development, Champion Programs, Keynotes,
                and Education.
              </p>
            </div>
            {/* Row 3: Image 5 | Learning */}
            <div className="overflow-hidden rounded-lg aspect-[4/3]" data-id="element-501">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/q6VZi7oxN6sFYqfkPq1e52/Principles_Image_5.jpg" alt="Spiral shell pattern with natural texture and earth tones" className="w-full h-full object-cover" data-id="element-502" />
            </div>
            <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col justify-center" data-id="element-503">
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-504">
                Learning & Enablement
              </h3>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-505">
                Making growth continuous—and practical.
              </p>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-506">
                We help you build the structure for learning that scales,
                sticks, and supports transformation at every level.
              </p>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-507">
                Services: L&D Strategy and Training Design.
              </p>
            </div>
            {/* Row 4: Image 6 | Measurement */}
            <div className="overflow-hidden rounded-lg aspect-[4/3]" data-id="element-508">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/nmBochXfcLD2kSD9oYTU77/Principles_Image_6.jpg" alt="Motion blur of people in modern architectural space" className="w-full h-full object-cover" data-id="element-509" />
            </div>
            <div className="bg-white rounded-lg p-6 md:p-8 flex flex-col justify-center" data-id="element-510">
              <h3 className="font-poppins font-normal text-xl md:text-2xl text-black mb-4" data-id="element-511">
                Measurement & Insight
              </h3>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-512">
                Tracking what matters—and act on what you see.
              </p>
              <p className="font-inter text-base leading-relaxed text-black mb-3" data-id="element-513">
                We surface behavioral signals and team-level insights to help
                you focus on what's working, what's stalling, and where to go
                next.
              </p>
              <p className="font-inter text-base leading-relaxed text-black" data-id="element-514">
                Services: Agility Diagnostics, Team Measurement, and Behavioral
                Signal Tracking.
              </p>
            </div>
          </div>
        </Section>
        {/* Closing Section */}
        <Section bgColor="lightgray" data-id="element-515">
          <div className="max-w-4xl mx-auto text-center" data-id="element-516">
            <h2 className="font-poppins font-normal text-3xl md:text-4xl text-black mb-6" data-id="element-517">
              You don't need another plan.
            </h2>
            <p className="font-inter text-base md:text-lg leading-relaxed text-black" data-id="element-518">
              You need the momentum to move it forward. We help you turn
              strategy into motion, and make change something people can believe
              in, build on, and carry.
            </p>
          </div>
        </Section>
        {/* CTA Band */}
        <LimeBandCTA text="Ready to get started?" linkText="Let's Connect" href="#" data-id="element-519" />
      </main>
      <Footer data-id="element-520" />
    </div>;
}